import React from 'react';

/** sessionStorage key holding the timestamp (ms) of the last automatic chunk-error reload. */
const RELOAD_TIMESTAMP_KEY = 'chunkErrorReloadedAt';

/** Minimum time (ms) between automatic reloads, to avoid a reload loop when a chunk is truly unavailable. */
const RELOAD_WINDOW_MS = 60000;

type Props = {
  children: React.ReactNode;
};

type State = {
  hasChunkError: boolean;
};

/**
 * Determines whether an error is a webpack lazy-chunk load failure.
 *
 * Covers the `ChunkLoadError` name set by webpack's JSONP loader as well as the
 * "Loading chunk N failed" / "Loading CSS chunk N failed" message variants.
 *
 * @param {Error} error - The error caught by the boundary.
 * @returns {boolean} True when the error came from a failed dynamic chunk load.
 */
export const isChunkLoadError = (error: Error): boolean =>
  error.name === 'ChunkLoadError' || /loading (css )?chunk .+ failed/i.test(error.message || '');

/**
 * Error boundary that recovers from ChunkLoadError.
 *
 * When a deploy replaces the hashed chunk files while a user is mid-session, lazy route
 * chunks referenced by the loaded app can fail to load. This boundary catches that specific
 * failure and reloads the page once (guarded by a sessionStorage timestamp) so the browser
 * picks up the new index.html and chunk manifest. If a reload was already attempted within
 * the last minute, it renders a manual-refresh prompt instead of looping. All other errors
 * are re-thrown so they surface exactly as they did without this boundary.
 */
export class ChunkErrorBoundary extends React.Component<Props, State> {
  public state: State = { hasChunkError: false };

  /**
   * Flags chunk-load failures in state so render() can show the fallback while reloading.
   *
   * @param {Error} error - The error thrown during rendering.
   * @returns {State | null} New state for chunk errors; null (no state change) otherwise.
   */
  public static getDerivedStateFromError(error: Error): State | null {
    return isChunkLoadError(error) ? { hasChunkError: true } : null;
  }

  /**
   * Reloads the page once for chunk-load failures; re-throws every other error.
   *
   * @param {Error} error - The error thrown during rendering.
   * @throws {Error} The original error, when it is not a chunk-load failure.
   */
  public componentDidCatch(error: Error): void {
    if (!isChunkLoadError(error)) {
      throw error;
    }
    let lastReloadAt = 0;
    try {
      lastReloadAt = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY)) || 0;
    } catch {
      // sessionStorage unavailable — fall through and reload without loop protection state.
    }
    if (Date.now() - lastReloadAt > RELOAD_WINDOW_MS) {
      try {
        window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
      } catch {
        return; // Cannot record the reload — do not risk a reload loop; leave the fallback UI up.
      }
      window.location.reload();
    }
  }

  /**
   * Renders children normally, or a refresh prompt after a chunk-load failure.
   *
   * @returns {React.ReactNode} The wrapped children or the fallback prompt.
   */
  public render(): React.ReactNode {
    if (this.state.hasChunkError) {
      return (
        <div style={{ padding: '48px 16px', textAlign: 'center' }}>
          <p>This application has been updated since this page was loaded.</p>
          <button onClick={() => window.location.reload()} type='button'>
            Refresh to continue
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
